import { parsePassCode } from '@sentra/contracts';
import { Flashlight, FlashlightOff } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/design/components/button';
import { Label } from '@/design/components/label';
import { NativeSelect } from '@/design/components/native-select';
import type { MediaDevicesLike } from '@/features/persons/images/camera';
import { useCamera } from '@/features/persons/images/camera';
import type { DeviceStorage } from '@/features/persons/images/devices';
import type { LoadedDetector } from '@/features/scan/detector';
import { loadCodeDetector } from '@/features/scan/detector';

/**
 * Reading a pass with the terminal's camera, for a desk that has no hardware scanner.
 *
 * **It reuses the registration screen's camera** — the same hook, the same remembered device, the
 * same four failure states. A second implementation of "open a camera" would be a second set of
 * bugs in the four cases an operator actually meets, and a second place that forgets to stop the
 * stream when the screen goes away. What is added here is the decode loop and the torch.
 *
 * **The decoder is loaded when the camera is opened, not when the application starts.** Most of
 * this product's scans arrive through the hardware wedge (AD-32); the megabyte of WebAssembly
 * behind `detector.ts` is for the terminal that has none, and on a browser with a native
 * `BarcodeDetector` it is never fetched at all.
 *
 * **A decode stops the loop.** A band held in front of a camera decodes thirty times a second, and
 * a loop that kept going would submit the same movement until somebody moved their hand. The caller
 * decides what happens next; reopening the camera is what starts another read.
 *
 * **The viewfinder is a PANEL, not a stage.** The `<video>` is `aspect-video w-full`, and the card
 * it sits in has no width of its own — so on a 1920-pixel terminal it rendered about 1584 by 891,
 * which pushed the resolved person and the two direction buttons off the screen while somebody was
 * standing at the door. The cap is on this panel rather than on the `<video>` alone, so the status
 * line, the device picker and the torch stay the width of the picture instead of running the card
 * beneath a small image. At the 1280-pixel viewport the browser suites pin, 32rem is a little over
 * half the card. The `unsupported` branch below is deliberately NOT capped: it is a sentence to
 * read, and a paragraph reads better at the card's width than at a viewfinder's.
 *
 * **A browser that cannot decode gets a sentence, not a spinner.** There is a real population here:
 * `BarcodeDetector` is native in Chromium, absent in Firefox and Safari, and the WebAssembly
 * fallback needs `'wasm-unsafe-eval'` in the CSP and a same-origin asset. When any of that is
 * missing the operator is told to use the scanner or type the code, which are both still there.
 */

/** How often a frame is offered to the decoder. Fast enough to feel instant, slow enough to idle. */
const DECODE_INTERVAL_MS = 200;

/** What the decoder is doing, which is a different question from what the camera is doing. */
type DecoderState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly loaded: LoadedDetector }
  | { readonly kind: 'unsupported' };

/**
 * The torch, which the DOM types do not describe.
 *
 * `torch` is a real constraint in the Media Capture specification's image-capture extensions and is
 * implemented on Android Chrome, which is the platform a handheld gate terminal runs; the
 * TypeScript DOM library does not carry it. Narrowing through a declared shape rather than reaching
 * for `any` keeps the cast to one place and keeps what is being assumed written down.
 */
interface TorchTrack {
  getCapabilities?: () => { torch?: boolean };
  applyConstraints: (constraints: { advanced: readonly { torch: boolean }[] }) => Promise<void>;
}

function torchTrack(stream: MediaStream): TorchTrack | null {
  const [track] = stream.getVideoTracks();
  if (track === undefined) {
    return null;
  }
  const capable = track as unknown as TorchTrack;
  return capable.getCapabilities?.().torch === true ? capable : null;
}

export function ScanCamera({
  media,
  storage,
  onCode,
  loadDetector = loadCodeDetector,
}: Readonly<{
  /** `null` when this browser exposes no camera API at all. */
  media: MediaDevicesLike | null;
  storage: DeviceStorage;
  /** A complete, valid code read from a frame. */
  onCode: (code: string) => void;
  /** Injected, because `jsdom` runs no WebAssembly and decodes no video. */
  loadDetector?: () => Promise<LoadedDetector>;
}>): ReactNode {
  const { t } = useTranslation();
  const camera = useCamera(media, storage);
  const [decoder, setDecoder] = useState<DecoderState>({ kind: 'loading' });
  const [torchOn, setTorchOn] = useState(false);
  const video = useRef<HTMLVideoElement | null>(null);
  const found = useRef(false);
  /** Whether a decode is in flight. See {@link detect} for why `found` alone is not enough. */
  const decoding = useRef(false);
  const deviceFieldId = useId();

  const live = camera.state.kind === 'live';
  const stream = camera.state.kind === 'live' ? camera.state.stream : null;
  const torch = stream === null ? null : torchTrack(stream);

  const { open, close } = camera;

  // The camera and the decoder are opened together: a preview with no decoder behind it is a
  // viewfinder that never finds anything, which reads as a broken scanner rather than as a browser
  // that cannot decode.
  useEffect(() => {
    open();
    let current = true;
    void loadDetector().then(
      (loaded) => {
        if (current) {
          setDecoder({ kind: 'ready', loaded });
        }
      },
      () => {
        if (current) {
          setDecoder({ kind: 'unsupported' });
        }
      },
    );
    return () => {
      current = false;
      close();
    };
  }, [close, loadDetector, open]);

  useEffect(() => {
    const element = video.current;
    if (element === null || stream === null) {
      return;
    }
    element.srcObject = stream;
    // Wrapped rather than chained: `play()` is specified to return a promise and several
    // environments this application is tested and run in return nothing at all, so calling
    // `.catch` on the result directly turns a working preview into a render-time crash.
    void Promise.resolve(element.play()).catch(() => {
      // A refused `play()` leaves a still frame. The decode loop reads whatever the element holds,
      // so it still works on that frame, and the camera's own states already say everything
      // actionable about why a preview is not moving.
    });
  }, [stream]);

  const detect = useCallback(
    async (detector: LoadedDetector['detector']): Promise<void> => {
      const element = video.current;
      // **`decoding` as well as `found`, because `found` is read before an `await` and written
      // after it.** The interval fires every `DECODE_INTERVAL_MS` and a WebAssembly decode on a
      // low-end terminal takes longer than that, so two invocations could both pass the `found`
      // check before either had set it — and one band in front of one camera produced two `onCode`
      // calls: two resolve requests, two `scan_attempts` rows and two consumptions of the address
      // budget for a single physical scan. The in-flight flag closes that window and also stops
      // slow decodes piling up behind each other.
      if (element === null || found.current || decoding.current) {
        return;
      }
      decoding.current = true;
      try {
        const codes = await detector.detect(element);
        for (const candidate of codes) {
          const parsed = parsePassCode(candidate.rawValue);
          if (parsed.ok) {
            // The first valid code ends the loop. A band held in front of a camera decodes thirty
            // times a second, and a reader that kept going would submit the same movement until
            // somebody moved their hand.
            found.current = true;
            onCode(parsed.code);
            return;
          }
        }
      } finally {
        decoding.current = false;
      }
    },
    [onCode],
  );

  useEffect(() => {
    if (!live || decoder.kind !== 'ready') {
      return undefined;
    }
    const { detector } = decoder.loaded;
    const timer = setInterval(() => {
      void detect(detector).catch(() => {
        // One frame that could not be decoded is ordinary: a hand moved, the focus hunted. The
        // loop keeps going, because stopping on a bad frame would make the scanner give up the
        // moment somebody adjusted the band.
      });
    }, DECODE_INTERVAL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [decoder, detect, live]);

  const toggleTorch = useCallback(() => {
    if (torch === null) {
      return;
    }
    const next = !torchOn;
    void torch.applyConstraints({ advanced: [{ torch: next }] }).then(
      () => {
        setTorchOn(next);
      },
      () => {
        // A refused constraint means this device will not do it after all. Leaving the control
        // where it was is the honest answer, because the lamp did not move.
      },
    );
  }, [torch, torchOn]);

  if (decoder.kind === 'unsupported') {
    return (
      <p role="alert" className="rounded-lg border border-warning bg-warning-surface p-3 text-sm">
        {t('scan.camera.unsupported')}
      </p>
    );
  }

  return (
    <div className="grid w-full max-w-lg gap-3">
      <div className="relative overflow-hidden rounded-lg border bg-muted">
        <video
          ref={video}
          aria-label={t('scan.camera.preview')}
          className="aspect-video w-full object-cover"
          muted
          playsInline
        />
        {!live && (
          <p className="absolute inset-0 grid place-items-center p-4 text-center text-sm">
            {/*
              The interpolation is supplied, because one of these messages has one: a camera that
              refused to open names the platform's own error, and a call site that filled nothing
              would put `{{name}}` on screen in front of an operator.
            */}
            {t(`scan.camera.${cameraMessage(camera.state.kind)}`, {
              name: camera.state.kind === 'failed' ? camera.state.name : '',
            })}
          </p>
        )}
      </div>

      <p role="status" className="text-sm text-muted-foreground">
        {t(decoder.kind === 'loading' ? 'scan.camera.loading' : 'scan.camera.scanning')}
      </p>

      {camera.options.length > 1 && (
        <div className="grid gap-1">
          <Label htmlFor={deviceFieldId}>{t('scan.camera.device')}</Label>
          <NativeSelect
            id={deviceFieldId}
            value={camera.selected ?? ''}
            onChange={(event) => {
              camera.select(event.target.value);
            }}
          >
            {camera.options.map((option) => (
              <option key={option.deviceId} value={option.deviceId}>
                {option.label}
              </option>
            ))}
          </NativeSelect>
        </div>
      )}

      {torch !== null && (
        <Button type="button" variant="outline" onClick={toggleTorch}>
          {torchOn ? <FlashlightOff aria-hidden /> : <Flashlight aria-hidden />}
          {t(torchOn ? 'scan.camera.torchOff' : 'scan.camera.torchOn')}
        </Button>
      )}
    </div>
  );
}

/**
 * The catalogue entry for a camera that is not live.
 *
 * A total map over the hook's own states rather than a switch with a default, so a state added to
 * `useCamera` is a missing key the compiler names instead of a blank panel at a desk.
 */
const CAMERA_MESSAGES = {
  unsupported: 'unsupported',
  idle: 'loading',
  opening: 'loading',
  denied: 'denied',
  no_camera: 'noCamera',
  lost: 'lost',
  failed: 'failed',
  live: 'scanning',
} as const;

function cameraMessage(
  kind: keyof typeof CAMERA_MESSAGES,
): (typeof CAMERA_MESSAGES)[keyof typeof CAMERA_MESSAGES] {
  return CAMERA_MESSAGES[kind];
}
